import Container from '@cloudscape-design/components/container'
import Header from '@cloudscape-design/components/header'
import Box from '@cloudscape-design/components/box'
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import type { CallResult, NetworkProbeResult } from '@/types'
import { useDesignTokens } from '@/hooks/useDesignTokens'
import {
  colorTextBodyDefault,
} from '@cloudscape-design/design-tokens'

interface Props { results: CallResult[]; probes: NetworkProbeResult[] }

export function ClientLatencyPanel({ results, probes }: Props) {
  const successful = results.filter((r) => r.success)
  if (successful.length === 0) {
    return (
      <Container header={<Header>Client Latency Breakdown</Header>}>
        <Box textAlign="center" color="text-body-secondary" padding="l">
          Run a benchmark to see client-side latency breakdown
        </Box>
      </Container>
    )
  }

  const probeTotalByHost = new Map<string, number>()
  for (const p of probes) {
    if (!p.error) probeTotalByHost.set(p.host, p.dnsLookupMs + p.tcpConnectMs + p.tlsHandshakeMs + p.proxyConnectMs)
  }

  const byProfile = new Map<string, CallResult[]>()
  for (const r of successful) {
    const list = byProfile.get(r.profileName) ?? []
    list.push(r)
    byProfile.set(r.profileName, list)
  }

  const avg = (vals: number[]) => vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0
  const tokens = useDesignTokens()

  const data = Array.from(byProfile.entries()).map(([name, calls]) => {
    const avgTtftMs = avg(calls.map((c) => c.ttftSec * 1000))
    const sampleCall = calls[0]
    let probeTotal = 0
    if (sampleCall.profileType === 'anthropic-direct') {
      probeTotal = probeTotalByHost.get('api.anthropic.com') ?? 0
    } else {
      for (const [host, total] of probeTotalByHost) {
        if (host.includes('bedrock-runtime')) { probeTotal = total; break }
      }
    }

    return {
      profile: name,
      'SDK Init': Math.round(avg(calls.map((c) => c.clientLatency.sdkInitMs)) * 10) / 10,
      'Request Build': Math.round(avg(calls.map((c) => c.clientLatency.requestBuildMs)) * 10) / 10,
      'Client Processing': Math.round(avg(calls.map((c) => c.clientLatency.clientProcessingMs)) * 10) / 10,
      'RT Overhead': Math.round(Math.max(0, avgTtftMs - probeTotal) * 10) / 10
    }
  })

  return (
    <Container header={<Header>Client Latency Breakdown (ms)</Header>}>
      <ResponsiveContainer width="100%" height={Math.max(150, data.length * 60 + 60)}>
        <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20 }}>
          <XAxis type="number" stroke={tokens.axisStroke} />
          <YAxis type="category" dataKey="profile" width={200} tick={{ fill: tokens.axisLabelColor, fontSize: 12 }} />
          <Tooltip contentStyle={{ backgroundColor: tokens.tooltipBg, border: `1px solid ${tokens.gridColor}` }} labelStyle={{ color: colorTextBodyDefault }} />
          <Legend />
          <Bar dataKey="SDK Init" stackId="a" fill={tokens.seriesPalette[0]} />
          <Bar dataKey="Request Build" stackId="a" fill={tokens.seriesPalette[1]} />
          <Bar dataKey="Client Processing" stackId="a" fill={tokens.seriesPalette[2]} />
          <Bar dataKey="RT Overhead" stackId="a" fill={tokens.seriesPalette[3]} />
        </BarChart>
      </ResponsiveContainer>
    </Container>
  )
}
